import { useState } from "react";
import { FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";
import { download, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Spinner } from "@/components/common";
import { MultiSelect } from "@/components/MultiSelect";
import { useApp } from "@/context/AppContext";
import { rangeParams } from "./BrandAnalytics";

const PRESETS = [["yesterday", "Yesterday"], ["last7", "Last 7 days"], ["this_month", "This month"], ["last_month", "Last month"], ["custom", "Custom"]];
const span = (f, t) => (f === t ? fmtDate(f) : `${fmtDate(f)} – ${fmtDate(t)}`);

const Download = ({ brand, s, fmt, range }) => {
  const [busy, setBusy] = useState(false);
  const Icon = fmt === "pdf" ? FileText : FileSpreadsheet;
  const go = async () => {
    setBusy(true);
    try {
      const slug = brand.toLowerCase().replace(/[^a-z]+/g, "-");
      await download("/analytics/export", { ...rangeParams(brand, s), format: fmt }, `${slug}${s.dept ? `-${s.dept.toLowerCase()}` : ""}-feedback-${range ? `${range.from}-to-${range.to}` : "report"}.${fmt}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button variant="outline" className="h-10 rounded-xl" onClick={go} disabled={busy || (s.preset === "custom" && !(s.from && s.to))} data-testid={`download-${fmt}-button`}>
      {busy ? <Spinner className="mr-2 h-4 w-4" /> : <Icon className="mr-2 h-4 w-4" />}{fmt === "pdf" ? "PDF" : "Excel"}
    </Button>
  );
};

const OutletPicker = ({ brand, s, update }) => {
  const { meta } = useApp();
  const options = (meta?.outlets || []).filter((o) => o.brand === brand && o.active !== false).map((o) => o.name);
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="outlet-picker">
      <MultiSelect label="Outlets" options={options} value={s.outlets || []} onChange={(v) => update({ outlets: v })} testid="analytics-outlet-filter" />
      <span className="text-sm text-slate-500" data-testid="outlet-scope-label">{s.outlets?.length ? `${s.outlets.length} of ${options.length} outlets` : `All ${options.length} outlets`}</span>
      {!!s.outlets?.length && <button className="text-sm font-medium text-indigo-600 hover:underline" onClick={() => update({ outlets: [] })} data-testid="outlet-select-all">Select all</button>}
    </div>
  );
};

const TEAMS = [["", "Both teams"], ["FOH", "FOH · Service"], ["BOH", "BOH · Kitchen"]];

const TeamToggle = ({ s, update }) => (
  <div className="no-scrollbar flex w-fit gap-1 overflow-x-auto rounded-xl bg-slate-100 p-1" role="radiogroup" data-testid="team-toggle">
    {TEAMS.map(([k, l]) => (
      <button key={k || "all"} role="radio" aria-checked={(s.dept || "") === k} onClick={() => update({ dept: k })} data-testid={`team-${(k || "all").toLowerCase()}`}
        className={`h-9 shrink-0 rounded-lg px-3 text-sm font-medium transition-colors ${(s.dept || "") === k ? (k === "BOH" ? "bg-orange-600 text-white" : k === "FOH" ? "bg-sky-600 text-white" : "bg-white text-slate-900 shadow-sm") : "text-slate-500 hover:text-slate-800"}`}>{l}</button>
    ))}
  </div>
);

export const RangeBar = ({ brand, s, update, range }) => (
  <div className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-4 lg:flex-row lg:items-center lg:justify-between" data-testid="range-bar">
    <div className="space-y-3">
      <div className="no-scrollbar flex gap-1 overflow-x-auto rounded-xl bg-slate-100 p-1" role="radiogroup">
        {PRESETS.map(([k, l]) => (
          <button key={k} role="radio" aria-checked={s.preset === k} onClick={() => update({ preset: k })} data-testid={`range-preset-${k}`}
            className={`h-9 shrink-0 rounded-lg px-3 text-sm font-medium transition-colors ${s.preset === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>{l}</button>
        ))}
      </div>
      <TeamToggle s={s} update={update} />
      <OutletPicker brand={brand} s={s} update={update} />
      {s.preset === "custom" && (
        <div className="flex flex-wrap items-center gap-2">
          <Input type="date" value={s.from} onChange={(e) => update({ from: e.target.value })} className="h-10 w-[160px] rounded-xl" data-testid="custom-from-input" />
          <span className="text-sm text-slate-400">to</span>
          <Input type="date" value={s.to} onChange={(e) => update({ to: e.target.value })} className="h-10 w-[160px] rounded-xl" data-testid="custom-to-input" />
        </div>
      )}
      {range && (
        <p className="text-sm text-slate-500" data-testid="range-label">
          <b className="text-slate-800">{span(range.from, range.to)}</b>{s.compare && <> vs {span(range.prev_from, range.prev_to)}</>}
        </p>
      )}
    </div>
    <div className="flex flex-wrap items-center gap-3">
      <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
        <Switch checked={s.compare} onCheckedChange={(v) => update({ compare: v })} data-testid="compare-toggle" />Compare with previous period
      </label>
      <Download brand={brand} s={s} fmt="pdf" range={range} />
      <Download brand={brand} s={s} fmt="xlsx" range={range} />
    </div>
  </div>
);
